import React, { useEffect, useState } from "react";
import { Icon } from "@/components/icon";
import { fetchAllCategoryFields, usePath } from "../../../contexts/path/path";
import { useAuthState, useIsOwner } from "../../../contexts/auth/auth.provider";
import { CountersButtons } from "../../new-resolver/CountersButtons";
import css from "./.module.scss";
import Title from "../../Title";
import ListingImages from "../ListingImages";
import { financialFormat } from "../../../lib/helpers";
import { useMapListing } from "../../new-resolver/useFindListings";
import { AlertForUser, CLOSED_STATUS_MAP } from "../ListingScreen/alert";
import { DailyListingRules, DailyUi, DailyUiMobile } from "./index";
import ListingVideo from "../ListingScreen/listingVideo";
import dayjs from "dayjs";
import { RelatedListings } from "@/components/Listing/ListingScreen/relatedListings";
import {
  listingToEECItemObject,
  trackViewItem,
} from "@/lib/tracking/trackEECEvents";
import { NewSpecsComponent } from "@/components/Listing/table/newSpecsComponent";
import { Modal } from "@aqar-app/react-ui";
import {
  NextListingButtonDaily,
  PreviousListingButtonDaily,
} from "@/components/Listing/ListingScreen/generalControls";
import FavButton from "@/components/Listing/ListingScreen/ListingControls/favButton";
import ShareButton from "@/components/Listing/ListingScreen/ListingControls/shareButton";
import { OwnerControls } from "@/components/Listing/ListingScreen/ownerControls";
import { ShowMore } from "@/components/Listing/showMore";
import { useQuery } from "@apollo/client";
import { GET_LISTING_EXTENDED_DETAILS } from "@/graphql/queries/listing";
import { useDeviceViewPort } from "@/hooks/viewport";
import LikeButton from "@/components/Listing/ListingScreen/ListingControls/LikeButton";

export function FloatingDailyListingDetails({
  listing: _listing,
  onListingChange,
  frozenListings,
} = {}) {
  const [listing] = useMapListing(_listing);
  const {
    updateContext,
    daily_renting_filter,
    dailyData,
    categoriesDictionary,
  } = usePath();

  const listingType = categoriesDictionary[_listing.category]?.ga_listing_type;
  const propertyCategory =
    categoriesDictionary[_listing.category]?.ga_property_category;

  const [isMobile] = useDeviceViewPort();
  const { price, imgs, title, status, published } = listing;
  const isOpen = !CLOSED_STATUS_MAP[status];
  const { user } = useAuthState();

  const isOwner = useIsOwner(listing);
  const [fields, setFields] = useState({});

  useEffect(() => {
    fetchAllCategoryFields(listing.category).then(setFields);
  }, [listing.category]);

  useEffect(() => {
    trackViewItem(
      listingToEECItemObject(_listing, undefined, categoriesDictionary),
      {
        start_date: daily_renting_filter?.day_date?.gte
          ? dayjs(daily_renting_filter?.day_date?.gte)?.format("DD/MM/YYYY")
          : undefined,
        end_date: daily_renting_filter?.day_date?.lte
          ? dayjs(daily_renting_filter?.day_date?.lte)?.format("DD/MM/YYYY")
          : undefined,
        value: price,
      },
    );
  }, [_listing]);

  const { data } = useQuery(GET_LISTING_EXTENDED_DETAILS, {
    skip: !listing.id,
    variables: {
      id: listing.id,
    },
  });

  const extendedDetails = data?.Listing?.getListingExtendedDetails || {};

  return (
    <div className={css.root + " " + css.white + " " + css.floatingMap}>
      <Title />
      <div className={`${css.title} ${css.floating}`}>
        <h1>{title}</h1>
        <button onClick={() => onListingChange(undefined)} type="button">
          <Icon width={15} height={15} name="Close" />
        </button>
      </div>
      <div className={css.images}>
        <div className={css.controls}>
          {!isOwner && <LikeButton listing={listing} />}
          <FavButton listing={listing} />
          <ShareButton
            type="daily_rent_unit"
            listing={listing}
            listingType={listingType}
            propertyCategory={propertyCategory}
          />
          {false ? <OwnerControls listing={listing} /> : null}
        </div>
        {isOpen && imgs && (
          <ListingImages
            listing={listing}
            loaded={listing.loadedImage}
            listingType={listingType}
            propertyCategory={propertyCategory}
          />
        )}
      </div>
      <h2 className={css.price}>
        {dailyData?.price?.discounted_price ? (
          <>{financialFormat(dailyData?.price?.discounted_price)} ريال</>
        ) : (
          <button onClick={Modal.open(<DailyUiMobile listing={listing} />)}>
            الرجاء إختيار فترة الحجز لعرض الأسعار
          </button>
        )}
      </h2>

      <div className={css.flexEqual}>
        <div className={css.pickerWrapper}>
          <DailyUi listing={listing} />
        </div>
        {published && listing.content && (
          <div
            className={`  ${css.content}  ${css.card} ${css.bottomBorderOnly} ${css.flexOne}`}
          >
            <ShowMore lines={isMobile ? 2 : 10}>{listing.content}</ShowMore>
          </div>
        )}
      </div>
      {!published && (
        <AlertForUser
          user={user}
          alert_box={listing.alert_box}
          listingUserId={listing.user_id}
        />
      )}

      <NewSpecsComponent
        {...fields}
        listing={{
          ...listing,
          extended_details: {
            ...listing.extended_details,
            ...extendedDetails,
          },
        }}
      />

      <DailyListingRules
        listing={listing}
        dailyData={dailyData}
        extendedDetails={extendedDetails}
      />
      {!!listing?.has_video && (
        <ListingVideo
          videoItem={(listing?.videos || [])[0]}
          listingType={listingType}
          propertyCategory={propertyCategory}
        />
      )}
      <div className={css.changeControlls}>
        <PreviousListingButtonDaily
          onListingChange={(e) => {
            onListingChange(e.listing);
            updateContext({
              currentFrozenIndex: e.currentFrozenIndex,
            });
          }}
          frozenListings={frozenListings}
          listingType={listingType}
          propertyCategory={propertyCategory}
        >
          الإعلان السابق
        </PreviousListingButtonDaily>
        <NextListingButtonDaily
          onListingChange={(e) => {
            onListingChange(e.listing);
            updateContext({
              currentFrozenIndex: e.currentFrozenIndex,
            });
          }}
          frozenListings={frozenListings}
        >
          الإعلان التالي
        </NextListingButtonDaily>
      </div>

      <CountersButtons bottom />
      <RelatedListings onListingChange={onListingChange} listing={listing} />
    </div>
  );
}

export default FloatingDailyListingDetails;
